import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Film, Globe, Palette, Lightbulb, Heart, ExternalLink, Mail } from 'lucide-react';
import { heroData, statsData, brandData } from '../data/mock';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Master Services Data with icons
const masterServices = [
  {
    id: 'production',
    name: 'Production',
    icon: Film,
    description: 'Film, Weddings, Events & Real Estate',
    color: 'from-white/10 to-white/5',
    services: ['film-production', 'wedding-cinematography', 'real-estate-media', 'event-broadcasting']
  },
  {
    id: 'digital',
    name: 'Digital',
    icon: Globe,
    description: 'Web, SEO, Ads & Automation',
    color: 'from-white/5 to-white/10',
    services: ['website-development', 'seo-strategies', 'digital-advertising', 'ai-automations']
  },
  {
    id: 'creative',
    name: 'Creative',
    icon: Palette,
    description: 'Branding, Design & Motion',
    color: 'from-white/10 to-white/5',
    services: ['brand-identity', 'content-creation', 'graphic-design', 'motion-graphics']
  },
  {
    id: 'consulting',
    name: 'Consulting',
    icon: Lightbulb,
    description: 'Strategy, Tech & Growth',
    color: 'from-white/5 to-white/10',
    services: ['media-strategy', 'technology-advisory', 'automation-consulting', 'content-strategy']
  }
];

const HomePage = () => {
  const [stories, setStories] = useState([]);

  useEffect(() => {
    // Fetch stories for the preview section
    fetch(`${API_URL}/api/stories`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setStories(data.slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO 
        title="Creative Director, Cinematographer & Digital Strategist"
        description="CreativeDirectorKhan | KHAN is a creative agency specializing in content and strategy — film production, digital marketing, branding, web development, and consulting. Serving clients worldwide."
        keywords="creative director, cinematographer, film production, wedding videography, digital marketing, branding, web development, SEO, Hassaan Khan, worldwide"
        canonical="https://creativedirectorkhan.com/"
      />
      
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative px-8 md:px-12">
        {/* Decorative Elements */}
        <div className="absolute top-1/4 left-12 w-px h-32 bg-gradient-to-b from-transparent via-white/20 to-transparent hidden lg:block" />
        <div className="absolute bottom-1/4 right-12 w-px h-32 bg-gradient-to-b from-transparent via-white/20 to-transparent hidden lg:block" />
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          {/* Tagline */}
          <p className="text-white/50 text-xs md:text-sm tracking-[0.4em] uppercase mb-12">
            {brandData.tagline}
          </p>

          {/* Main Headline */}
          <h1 className="font-serif text-5xl md:text-7xl lg:text-[120px] font-light leading-[0.9] mb-8">
            <span className="text-white block">Crafting Visual</span>
            <span className="text-white/30 italic block mt-2">Stories</span>
          </h1>

          {/* Description */}
          <p className="text-white/50 text-lg md:text-xl max-w-xl mx-auto leading-relaxed mt-12 mb-16">
            {heroData.description}
          </p>

          {/* CTA - View Success Stories */}
          <Link
            to="/stories"
            className="inline-flex items-center gap-4 group"
          >
            <span className="text-white/70 text-sm tracking-[0.25em] uppercase group-hover:text-white transition-colors duration-500">View Success Stories</span>
            <span className="w-12 h-px bg-white/30 group-hover:w-20 group-hover:bg-white transition-all duration-500" />
            <ArrowRight className="w-4 h-4 text-white/50 group-hover:text-white group-hover:translate-x-2 transition-all duration-500" />
          </Link>
        </div>

        {/* Scroll Line */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4">
          <span className="text-white/30 text-xs tracking-widest uppercase rotate-90 origin-center">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent" />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-8 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-16">
            {statsData.map((stat, index) => (
              <div key={index} className="text-center group">
                <p className="font-serif text-6xl md:text-8xl font-light text-white/90 group-hover:text-white transition-colors duration-500">
                  {stat.value}
                </p>
                <p className="text-white/40 text-xs tracking-[0.3em] uppercase mt-4">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Master Services Gallery - Icon Grid */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-8 md:px-12">
          <div className="text-center mb-16">
            <p className="text-white/40 text-xs tracking-[0.4em] uppercase mb-4">What We Do</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-white">
              Our Expertise
            </h2>
          </div>
          
          {/* Services Icon Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {masterServices.map((service) => (
              <Link
                key={service.id}
                to={`/services#${service.id}`}
                className="group relative p-8 md:p-10 border border-white/10 bg-gradient-to-br hover:border-white/20 transition-all duration-500 text-center"
              >
                {/* Icon */}
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors duration-500">
                  <service.icon className="w-7 h-7 text-white/60 group-hover:text-white transition-colors duration-500" />
                </div>
                
                {/* Name */}
                <h3 className="font-serif text-xl md:text-2xl text-white mb-2 group-hover:text-white/90 transition-colors">
                  {service.name}
                </h3>
                
                {/* Description */}
                <p className="text-white/40 text-sm group-hover:text-white/60 transition-colors">
                  {service.description}
                </p>
                
                {/* Hover Arrow */}
                <div className="mt-4 flex items-center justify-center gap-2 text-white/30 group-hover:text-white/60 transition-all">
                  <span className="text-xs tracking-wider uppercase">Explore</span>
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
          
          {/* View All Services Link */}
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-3 text-white/50 hover:text-white transition-colors group"
            >
              <span className="text-sm tracking-wider uppercase">View All Services</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Wedding Cinematography - Referral to Crystal Media */}
      <section className="py-24 border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] via-transparent to-white/[0.02]" />
        <div className="max-w-4xl mx-auto px-8 md:px-12 relative text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Heart className="w-5 h-5 text-white/40" />
            <p className="text-white/40 text-xs tracking-[0.4em] uppercase">Wedding Films</p>
          </div>
          
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6">
            Your Wedding Day
            <span className="italic text-white/40 block mt-2">Reimagined on Film</span>
          </h2>
          
          <p className="text-white/60 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            For over a decade, we've been capturing the most precious moments of couples across Canada, USA, and worldwide destinations. Traditional, modern fusion, or destination weddings — we tell your love story with cinematic artistry.
          </p>
          
          {/* Crystal Media Link */}
          <a
            href="https://crystalmedia.ca"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-4 px-10 py-5 bg-white text-black text-sm tracking-[0.2em] uppercase hover:bg-white/90 transition-all group"
          >
            <span>Visit Crystal Media</span>
            <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          
          <p className="text-white/30 text-sm mt-6">
            Our dedicated wedding cinematography brand
          </p>
        </div>
      </section>

      {/* Philosophy Quote */}
      <section className="py-32 border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-white/[0.02] via-transparent to-white/[0.02]" />
        <div className="max-w-5xl mx-auto px-8 md:px-12 text-center relative">
          <span className="text-[200px] md:text-[300px] font-serif text-white/[0.03] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 select-none">
            &ldquo;
          </span>
          <p className="text-white/30 text-xs tracking-[0.4em] uppercase mb-10">Philosophy</p>
          <blockquote className="font-serif text-2xl md:text-4xl lg:text-5xl font-light text-white/80 leading-relaxed">
            Every project is a commitment to<br />
            <span className="italic text-white/40">precision</span> and <span className="italic text-white/40">artistry</span>.
          </blockquote>
          <Link
            to="/about"
            className="inline-flex items-center gap-3 text-white/40 hover:text-white/70 transition-all duration-500 group mt-16"
          >
            <span className="text-xs tracking-[0.25em] uppercase">About Me</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-500" />
          </Link>
        </div>
      </section>

      {/* Success Stories Preview */}
      {stories.length > 0 && (
        <section className="py-32 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-8 md:px-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16">
              <div>
                <p className="text-white/30 text-xs tracking-[0.4em] uppercase mb-4">Our Work</p>
                <h2 className="font-serif text-5xl md:text-6xl font-light text-white">Success Stories</h2>
              </div>
              <Link
                to="/stories"
                className="inline-flex items-center gap-3 text-white/40 hover:text-white/70 transition-all duration-500 group mt-8 md:mt-0"
              >
                <span className="text-xs tracking-[0.25em] uppercase">View All</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-500" />
              </Link>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {stories.map((story) => (
                <Link
                  key={story.slug}
                  to={`/stories/${story.slug}`}
                  className="group block"
                >
                  <div className="aspect-[4/3] overflow-hidden mb-4">
                    {story.hero_image ? (
                      <img 
                        src={story.hero_image} 
                        alt={story.client_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
                        <span className="font-serif text-4xl text-white/20">{story.client_name?.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-white/40 text-xs tracking-wider uppercase mb-2">Success Story</p>
                  <h3 className="font-serif text-2xl text-white group-hover:text-white/80 transition-colors">{story.client_name}</h3>
                  <p className="text-white/50 text-sm mt-1 line-clamp-1">{story.title}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-40 border-t border-white/5 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.01] to-transparent" />
        <div className="max-w-4xl mx-auto px-8 md:px-12 text-center relative">
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-white mb-8">
            Where Vision Becomes
            <span className="italic text-white/40"> Direction</span>
          </h2>
          <p className="text-white/60 text-lg mb-14 max-w-md mx-auto">
            Have a vision in mind?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-block px-14 py-5 border border-white/20 text-white/90 text-sm tracking-[0.25em] uppercase hover:bg-white hover:text-black hover:border-white transition-all duration-500"
            >
              Get in Touch
            </Link>
            <a
              href="mailto:hassaan@crystalmedia.ca"
              className="inline-flex items-center justify-center gap-3 px-10 py-5 text-white/60 text-sm tracking-[0.15em] uppercase hover:text-white transition-colors duration-500"
            >
              <Mail className="w-4 h-4" />
              Quick Inquiry
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
